<template>
    <app-layout>
        <template #icon>
            <div class="flex h-10 w-10 items-center justify-center">
                <Blocks class="zap-gradient" :size="32" />
            </div>
        </template>
        <template #title> {{ $t("integrations.title") }} </template>
        <template #description> {{ $t("integrations.description") }}</template>

        <template #actions>
            <AddIntegrationModal>
                <AddButton>{{ $t("integrations.add_integration") }}</AddButton>
            </AddIntegrationModal>
        </template>

        <!-- <nav class="mx-auto mt-6 flex max-w-7xl px-8" aria-label="Tabs">
            <a
                v-for="tab in tabs"
                :key="tab.name"
                :href="tab.href"
                :class="[
                    'inline-flex items-center px-3 py-2 text-center text-sm font-medium',
                    tab.current
                        ? ' rounded-lg bg-iron-200 text-center text-iron-700 '
                        : 'text-iron-500 hover:border-iron-300 hover:text-iron-700  ',
                ]"
            >
                {{ tab.name }}
                <span
                    v-if="tab.count !== undefined"
                    class="ml-2 rounded px-[5px] text-xs leading-[18px]"
                    :class="[
                        tab.current
                            ? 'bg-iron-700 text-white'
                            : 'bg-iron-200 text-iron-700',
                    ]"
                    >{{ tab.count }}</span
                >
            </a>
        </nav> -->

        <div
            v-if="integrations.length === 0"
            class="mx-auto mt-10 flex w-full max-w-xl flex-col items-center justify-center rounded-md bg-[#f4f4f5] p-10"
        >
            <h2 class="text-lg font-medium">
                {{ $t("integrations.empty_state.title") }}
            </h2>

            <div class="mt-4">
                <AddIntegrationModal>
                    <AddButton data-cy="create-new-day-off">
                        {{ $t("integrations.add_integration") }}</AddButton
                    >
                </AddIntegrationModal>
            </div>
        </div>

        <div
            class="mx-auto grid max-w-7xl gap-2.5 py-6 sm:px-6 md:grid-cols-2 lg:grid-cols-3 lg:px-8"
        >
            <GoogleIntegrationModal
                v-for="integration of googleIntegrations"
                :key="integration.id"
                :integration="integration"
            >
                <IntegrationCard selected has-sub>
                    <template #logo>
                        <GoogleLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.google.title") }}:
                        {{ integration?.data?.email }}
                    </template>

                    <template #description>
                        {{ $t("integrations.google.description") }}
                    </template>
                </IntegrationCard>
            </GoogleIntegrationModal>

            <AppleCalendarIntegrationModal
                v-for="integration of iCloudCalendarIntegrations"
                :key="integration.id"
                :integration="integration"
            >
                <IntegrationCard selected has-sub>
                    <template #logo>
                        <ICloudLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.icloud_calendar.title") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.icloud_calendar.description") }}
                    </template>
                </IntegrationCard>
            </AppleCalendarIntegrationModal>

            <EcomailIntegrationModal
                v-for="ecmIntegration of ecomailIntegrations"
                :key="ecmIntegration.id"
                :integration="ecmIntegration"
                removable
                @remove-integration="removeIntegration(ecmIntegration)"
            >
                <IntegrationCard selected has-sub>
                    <template #logo>
                        <EcomailLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.ecomail.title") }}:
                        {{ ecmIntegration.name }}
                    </template>

                    <template #description>
                        {{ $t("integrations.ecomail.description") }}
                    </template>
                </IntegrationCard>
            </EcomailIntegrationModal>

            <CloseIntegrationModal
                v-for="integration of closeIntegrations"
                :key="integration.id"
                :integration="integration"
                removable
                @remove-integration="removeIntegration(integration)"
            >
                <IntegrationCard selected has-sub>
                    <template #logo>
                        <CloseLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.close.title") }}:
                        {{ integration.name }}
                    </template>

                    <template #description>
                        {{ $t("integrations.close.description") }}
                    </template>
                </IntegrationCard>
            </CloseIntegrationModal>

            <SlackIntegrationModal
                v-for="integration of slackIntegrations"
                :key="integration.id"
                :integration="integration"
                removable
                @remove-integration="removeIntegration(integration)"
            >
                <IntegrationCard selected has-sub>
                    <template #logo>
                        <SlackLogo />
                    </template>

                    <template #name>
                        {{ $t("integrations.slack.title") }}
                    </template>

                    <template #description>
                        {{ $t("integrations.slack.description") }}
                    </template>
                </IntegrationCard>
            </SlackIntegrationModal>

            <RaynetIntegrationModal
                v-for="integration of raynetIntegrations"
                :key="integration.id"
                :integration="integration"
                removable
                @remove-integration="removeIntegration(integration)"
            >
                <IntegrationCard selected has-sub>
                    <template #logo>
                        <img
                            class="-mt-1 h-8"
                            src="https://d39xau2r7ekvda.cloudfront.net/application/assets/img/Raynet.png"
                        />
                    </template>

                    <template #name>
                        {{ $t("integrations.raynet.title") }}:
                        {{ integration.name }}</template
                    >

                    <template #description>
                        {{ $t("integrations.raynet.description") }}
                    </template>
                </IntegrationCard>
            </RaynetIntegrationModal>

            <FreeloIntegrationModal
                v-for="integration of freeloIntegrations"
                :key="integration.id"
                :integration="integration"
                removable
                @remove-integration="removeIntegration(integration)"
            >
                <IntegrationCard has-sub selected>
                    <template #logo>
                        <FreeloLogo />
                    </template>

                    <template #name> {{ integration.name }} </template>

                    <template #description>
                        {{ $t("integrations.freelo.description") }}
                    </template>
                </IntegrationCard>
            </FreeloIntegrationModal>

            <StripeIntegrationModal
                v-for="integration of stripeIntegrations"
                :key="integration.id"
                :integration="integration"
                :removable="true"
                @remove-integration="removeIntegration(integration)"
            >
                <IntegrationCard
                    :id="'integration-' + integration.id"
                    has-sub
                    selected
                >
                    <template #logo>
                        <StripeLogo class="-ml-1 w-16" />
                    </template>

                    <template #name> {{ integration.name }} </template>

                    <template #description>
                        {{ $t("integrations.stripe.description") }}
                    </template>
                </IntegrationCard>
            </StripeIntegrationModal>
        </div>
    </app-layout>
</template>

<script lang="ts" setup>
import AppLayout from "./../../../../Layouts/AppLayout.vue";
import EcomailIntegrationModal from "./components/EcomailIntegrationModal.vue";
import CloseIntegrationModal from "./components/CloseIntegrationModal.vue";
import AppleCalendarIntegrationModal from "./components/ICloudCalendarIntegrationModal.vue";
import { computed, onMounted } from "vue";
import SlackIntegrationModal from "./components/SlackIntegrationModal.vue";
import ICloudLogo from "./components/logos/ICloud.svg";
import EcomailLogo from "./components/logos/Ecomail.svg";
import SlackLogo from "./components/logos/Slack.svg";
import CloseLogo from "./components/logos/Close.svg";
import ZoomLogo from "./components/logos/Zoom.svg";
import GoogleLogo from "./components/logos/Google.svg";
import StripeLogo from "./components/logos/Stripe.svg";
import FreeloLogo from "./components/logos/Freelo.svg";
import route from "ziggy-js";
import { Blocks } from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import RaynetIntegrationModal from "./components/RaynetIntegrationModal.vue";
import axios from "axios";
import CalendlyMigrationModal from "@/Pages/ZapTime/App/Integrations/components/CalendlyMigrationModal.vue";
import IntegrationCard from "./components/IntegrationCard.vue";
import { defineProps, ref } from "vue";
import FreeloIntegrationModal from "@/Pages/ZapTime/App/Integrations/components/FreeloIntegrationModal.vue";
import GoogleIntegrationModal from "./components/GoogleIntegrationModal.vue";

import { router } from "@inertiajs/vue3";
import useConfirm from "../ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";
import AddIntegrationModal from "./components/AddIntegrationModal.vue";
import AddButton from "../Atomic/AddButton.vue";
import StripeIntegrationModal from "./components/StripeIntegrationModal.vue";
import { useUrlSearchParams } from "@vueuse/core";

const props = defineProps<{
    user: any;
    integrations: any[];
}>();

const { show } = useConfirm();
const { t } = useI18n();
const params = useUrlSearchParams();

function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

async function removeIntegration(integration: any) {
    const ok = await show({
        header:
            t("integrations.delete.header") +
                ": " +
                capitalizeFirstLetter(integration.type.toLowerCase()) +
                " " +
                integration?.name || "",
        message: t("integrations.delete.message"),
        confirm: t("integrations.delete.confirm"),
        cancel: t("integrations.delete.cancel"),
    });

    if (ok) {
        router.delete(`/integrations/${integration.id}`);
    }
}

const googleIntegrations = computed(() => {
    return props.integrations.filter((i) => i.type === "GOOGLE");
});

const iCloudCalendarIntegrations = computed(() => {
    return props.integrations.filter((i) => i.type === "ICLOUD_CALENDAR");
});

const slackIntegrations = computed(() => {
    return props.integrations.filter((i) => i.type === "SLACK");
});

const ecomailIntegrations = computed(() => {
    return props.integrations.filter((i) => i.type === "ECOMAIL");
});

const closeIntegrations = computed(() => {
    return props.integrations.filter((i) => i.type === "CLOSE");
});

const freeloIntegrations = computed(() => {
    return props.integrations.filter((i) => i.type === "FREELO");
});

const raynetIntegrations = computed(() => {
    return props.integrations.filter((i) => i.type === "RAYNET");
});

const stripeIntegrations = computed(() => {
    return props.integrations.filter((i) => i.type === "STRIPE");
});

const tabs = ref([
    {
        name: "Všechny",
        current: true,
        // count: 2,
        // href: route("app.dashboard.upcoming"),
        // current:
        //     route().current("app.dashboard") ||
        //     route().current("app.dashboard.upcoming"),
        // count: props.upcomingReservationsCount,
    },
    {
        name: "Kalendáře",
        current: false,
        // count: 2,
        // href: route("app.dashboard.upcoming"),
        // current:
        //     route().current("app.dashboard") ||
        //     route().current("app.dashboard.upcoming"),
        // count: props.upcomingReservationsCount,
    },
    // {
    //     name: t("bookings.pending"),
    //     href: "#",
    //     current: false,
    // },
    {
        name: "Správa hovorů",
        // href: route("app.dashboard.past"),
        // current: route().current("app.dashboard.past"),
        // count: props.pastReservationsCount,
    },
    {
        name: "CRM",
        // href: route("app.dashboard.cancelled"),
        // current: route().current("app.dashboard.cancelled"),
        // count: props.cancelledReservationsCount,
    },
    {
        name: "Analytika",
        // href: route("app.dashboard.cancelled"),
        // current: route().current("app.dashboard.cancelled"),
        // count: props.cancelledReservationsCount,
    },
    {
        name: "E-mailing",
        // href: route("app.dashboard.cancelled"),
        // current: route().current("app.dashboard.cancelled"),
        // count: props.cancelledReservationsCount,
    },
    {
        name: "Platby",
        // href: route("app.dashboard.cancelled"),
        // current: route().current("app.dashboard.cancelled"),
        // count: props.cancelledReservationsCount,
    },
    {
        name: "Automatizace",
        // href: route("app.dashboard.cancelled"),
        // current: route().current("app.dashboard.cancelled"),
        // count: props.cancelledReservationsCount,
    },
]);

onMounted(() => {
    if (params["integrationId"]) {
        const el = document.querySelector(
            "#integration-" + params["integrationId"],
        );

        if (el !== null) {
            // @ts-expect-error
            el.children[2].click();
        }
    }
});
</script>
